import "../../../App.css";
import React, {useCallback, useEffect, useState} from "react";
import {
    Button,
    Descriptions,
    Form,
    Input,
    Layout,
    message,
    Radio,
    Result,
    Typography
} from "antd";
import "antd/dist/reset.css";
import useAxios from "axios-hooks";
import {CheckoutSubmissionRequest} from "../../../types/CheckoutSubmissionRequest";
import {useOrderId} from "../../../hooks/UseOrderId";
import {Copyable} from "../../common/Copyable";
import {PaymentType} from "../../../App";
import {CardTypeEnum} from "../../../types/CardTypeEnum";
import {CheckoutStatusResult} from "../../../types/CheckoutStatusResult";
import {ProviderTypeEnum} from "../../../types/ProviderTypeEnum";
import WrappedCard from "../../antd/WrappedCard";

const {Content} = Layout;
const {Paragraph} = Typography;

const OverdueTime = 3 * 60 * 1000;
const PollingTime = 3 * 1000;
const stoppingStatuses: string[] = ["SUCCESS", "FAIL"];

const rules = {
    customerAccount: [
        {required: true, message: "Please input your mobile number."},
        {
            pattern: /^03\d{9}$/,
            message: "Please enter a valid mobile number, it should start with '03' followed by 9 digits."
        }
    ],
    customerName: [
        {required: true, message: "Please input your name."},
        {max: 64, message: "The length of name cannot exceed 64 chars."}
    ],
    customerEmail: [
        {required: true, message: "Please input your email."},
        {pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w+)+$/, message: "Please enter a valid email."},
    ],
};

const placeholders = {
    customerAccount: "03123456789",
    customerName: "Your Real Name",
    customerEmail: "your-email-address@example.com"
};

// NOTICE: GET目前是同步的，后面有可能会需要支援非同步，近来收银台后先转圈等到GET有结果

function PkQisstEasypaisaMode({getCheckoutInfoData}: PaymentType) {
    const id = useOrderId();
    const [{data: putData}, executePost] =
        useAxios<CheckoutSubmissionRequest>(
            {
                url: `/v2/checkout/${id}`,
                method: "POST"
            },
            {manual: true}
        );

    const [
        {
            data: getCheckoutResultData,
            loading: pollingLoading
        },
        pollingCheckoutResult
    ] = useAxios<CheckoutStatusResult>(`/v2/orders/${id}`);

    const fullForm = getCheckoutInfoData?.reserved1 === "FULL_FORM";

    const [submitCheckout, setSubmitCheckout] = useState(getCheckoutInfoData?.checkoutStatus === "SUBMITTED");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isOverdueTime, setIsOverdueTime] = useState<boolean>(false);

    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();

    const onFormFinish = (values: any) => {
        setIsSubmitting(true);
        executePost({
            data: {
                id: id,
                providerType: ProviderTypeEnum.WALLET,
                providerName: values.providerName,
                customerAccount: values.customerAccount,
                customerName: values.customerName,
                customerEmail: values.customerEmail
            }
        })
            .then(() => {
                setSubmitCheckout(true);
                startOverdueCountDown();
            })
            .catch((error) => {
                pollingCheckoutResult().then();

                const errorMsg = error?.response?.data?.title;
                if (!errorMsg.startsWith("Payment failed")) {
                    messageApi.error(errorMsg).then();
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const onFinishFailed = (errorInfo: any) => {
        console.log("Failed:", errorInfo);
        setIsSubmitting(false);
    };

    const onButtonClick = () => {
        setIsSubmitting(true);
        form.submit();
    };

    useEffect(() => {
        let pollingCountDown: NodeJS.Timeout;
        if (!isOverdueTime && submitCheckout) {
            if (getCheckoutResultData?.status === "PROCESSING") {
                pollingCountDown = setTimeout(function () {
                    pollingCheckoutResult().then();
                }, PollingTime);
            } else if (typeof getCheckoutResultData !== "undefined" && stoppingStatuses.includes(getCheckoutResultData.status)) {
                console.log("[Polling] end to polling.data", getCheckoutResultData);
            }
        }
        return () => {
            if (pollingCountDown) clearTimeout(pollingCountDown);
        };
    }, [submitCheckout, pollingLoading, pollingCheckoutResult, isOverdueTime, getCheckoutResultData]);

    // NOTICE: 三分截止
    let countDown: NodeJS.Timeout;
    const startOverdueCountDown = useCallback(() => {
        countDown = setTimeout(function () {
            setIsOverdueTime(true);
        }, OverdueTime);
    }, []);

    useEffect(() => {
        return () => {
            clearTimeout(countDown);
        };
    });

    return (
        <>
            {contextHolder}

            {/*NOTICE: 延遲提交且在建立訂單或取得收銀台細節時失敗*/}
            {getCheckoutResultData?.status === "FAIL" && (
                <Result
                    status="error"
                    title="Payment Failed"
                    subTitle="Please close this page and try again."
                />
            )}

            {/*NOTICE: 訂單已完成*/}
            {getCheckoutResultData?.status === "SUCCESS" && (
                <Result status="success" title="Successfully Paid"/>
            )}

            {/*NOTICE: 訂單未完成*/}
            {getCheckoutResultData?.status === "PROCESSING" && getCheckoutInfoData?.checkoutStatus === "VISITED" &&
                !submitCheckout && (
                    <Content>
                        <WrappedCard cardTitle="Information">
                            <Descriptions title="Amount" bordered>
                                <Descriptions.Item label="Amount Payable">
                                    <Typography.Title
                                        copyable={{icon: <Copyable/>}}
                                        level={5}
                                        style={{margin: 0}}
                                    >
                                        {getCheckoutInfoData?.orderAmount}
                                    </Typography.Title>
                                </Descriptions.Item>
                            </Descriptions>
                        </WrappedCard>

                        <WrappedCard cardTitle="Please input your payment information">
                            <Form
                                form={form}
                                initialValues={{providerName: CardTypeEnum.EasyPaisa}}
                                onFinish={onFormFinish}
                                onFinishFailed={onFinishFailed}
                            >
                                <Form.Item label="Select Payment Type" name="providerName">
                                    <Radio.Group>
                                        <Radio.Button value={CardTypeEnum.EasyPaisa}>
                                            EasyPaisa
                                        </Radio.Button>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item
                                    label="Mobile Number"
                                    name="customerAccount"
                                    rules={rules.customerAccount}
                                >
                                    <Input placeholder={placeholders.customerAccount}/>
                                </Form.Item>
                                {fullForm &&
                                  <>
                                    <Form.Item
                                      label="Payment Account Holder Name"
                                      name="customerName"
                                      rules={rules.customerName}
                                    >
                                      <Input
                                        placeholder={placeholders.customerName}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Email"
                                      name="customerEmail"
                                      rules={rules.customerEmail}
                                    >
                                      <Input
                                        placeholder={placeholders.customerEmail}
                                      />
                                    </Form.Item>
                                  </>
                                }
                                <Form.Item>
                                    <Button type="primary" onClick={onButtonClick} disabled={isSubmitting}>
                                        {isSubmitting ? "Submitting..." : "Submit"}
                                    </Button>
                                </Form.Item>
                            </Form>
                        </WrappedCard>
                    </Content>
                )}

            {/*NOTICE: 使用者填寫完畢送出，會看到等待付款中狀態頁面。*/}
            {submitCheckout &&
                !isOverdueTime &&
                getCheckoutResultData?.status === "PROCESSING" && (
                    <Result
                        status="info"
                        title="Follow the Steps"
                    >
                        <Paragraph style={{fontSize: "16px"}}>
                            <ol>
                                <li>Open your EasyPaisa app</li>
                                <li>Click the top left menu</li>
                                <li>Click "My Approvals"</li>
                                <li>Accept payment</li>
                            </ol>
                        </Paragraph>
                        <iframe
                            width="100%"
                            height="490px"
                            src="https://www.youtube.com/embed/K3Hm0qTQDy8"
                            title="QQ Pay - how to do"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                            allowFullScreen={true}
                        ></iframe>
                    </Result>
                )}

            {/*NOTICE: 系統超過三分鐘後，使用者會看到訂單有問題之類訊息(後端之後會動態調整文案)。*/}
            {submitCheckout &&
                isOverdueTime &&
                getCheckoutResultData?.status === "PROCESSING" && (
                    <Result
                        status="warning"
                        title="We have not received your payment yet. If the transaction is delayed, please reach out to customer service if there is no further progress within two hours."
                    />
                )}
        </>
    );
}

export default PkQisstEasypaisaMode;
